/**
 * Pure tracking rules for the web funnel (plan Part A: A3, A4). No DOM, no
 * imports — storage is injected — so `npm test` can exercise it with
 * node --test (see test/trackingCore.test.cjs).
 */

// ───────────────────────── A3: purchase analytics ─────────────────────────

/** Plans that start with a free trial ($0 at checkout, first charge ~day 7). */
export const TRIAL_PLAN_IDS: readonly string[] = ['1year']

export function isTrialPlan(planId: string | null | undefined): boolean {
  return !!planId && TRIAL_PLAN_IDS.includes(planId)
}

export interface PurchaseAnalyticsPlan {
  /** GA4 event: trials and immediate purchases are separate events. */
  ga4Event: 'funnel_trial_started' | 'funnel_immediate_purchase'
  /** 0 for a trial — no money changed hands. */
  ga4Value: number
  /**
   * Google Ads purchase conversion (AW-17896777113/auA1CN6BrOwbEJnL7NVC):
   * immediate purchases only, never trials, and never when the Ads tag is
   * not loaded (QA sessions).
   */
  fireAdsConversion: boolean
}

/**
 * Replaces the retired `funnel_purchase_complete`, which fired for every
 * checkout (trials included, at the annual price) to both GA4 and the Google
 * Ads purchase action.
 */
export function planPurchaseAnalytics(input: { isTrial: boolean; price: number; adsEnabled: boolean }): PurchaseAnalyticsPlan {
  if (input.isTrial) {
    return { ga4Event: 'funnel_trial_started', ga4Value: 0, fireAdsConversion: false }
  }
  return { ga4Event: 'funnel_immediate_purchase', ga4Value: input.price, fireAdsConversion: input.adsEnabled }
}

// ───────────────────────── A4: enrollment ─────────────────────────

export const ENROLLMENT_KEY = 'kidneypal_enrollment'

/**
 * Experiment label per variant. Part B (docs/PLAN-web-funnel-meta-retest.md §B1):
 * meta-short shows offer_v2 (one annual free-trial card + "See other plans"),
 * labelled meta_short_offer_v2. Enrollments written before it keep
 * meta_short_v1 (the June 3-plan offer).
 *
 * Organic A/B (docs/FUNNEL-ORGANIC-V2.md): control = organic_v1, treatment =
 * organic_v2. Enrollment records written before this change keep the bare
 * 'organic' label (records are never rewritten), which cleanly marks
 * pre-test visitors.
 */
const EXPERIMENT_VERSIONS: Record<string, string> = {
  organic: 'organic_v1',
  'organic-v2': 'organic_v2',
  'meta-short': 'meta_short_offer_v2',
  'meta-advertorial': 'meta_advertorial_v1',
}

const DEFAULT_EXPERIMENT_VERSION = EXPERIMENT_VERSIONS.organic

export function experimentVersionFor(variant: string | null | undefined): string {
  return EXPERIMENT_VERSIONS[variant || 'organic'] || DEFAULT_EXPERIMENT_VERSION
}

/**
 * Written once per browser on first landing and never overwritten. Not a
 * customer identity (storage can be cleared, devices differ) — it rides on
 * every analytics event and into Paddle custom_data at checkout, so the
 * enrollment that counts is the one attached to the purchase.
 */
export interface EnrollmentRecord {
  enrollment_id: string
  first_seen_at: string
  utm_campaign: string | null
  variant: string
  experiment_version: string
}

export interface KVStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

function isEnrollment(v: unknown): v is EnrollmentRecord {
  const r = v as EnrollmentRecord
  return !!r && typeof r.enrollment_id === 'string' && !!r.enrollment_id && typeof r.first_seen_at === 'string'
    && typeof r.variant === 'string' && typeof r.experiment_version === 'string'
}

export function readEnrollment(storage: KVStorage | null): EnrollmentRecord | null {
  if (!storage) return null
  try {
    const raw = storage.getItem(ENROLLMENT_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return isEnrollment(parsed) ? parsed : null
  } catch {
    return null
  }
}

/**
 * Return the stored enrollment, or create + store it. An existing record is
 * NEVER overwritten (a later ad click from the same browser keeps its first
 * enrollment — Part B reports those separately). Unusable storage yields an
 * unsaved record for this page load.
 */
export function ensureEnrollment(
  storage: KVStorage | null,
  init: { nowMs: number; utmCampaign: string | null | undefined; variant: string; newId: () => string },
): EnrollmentRecord {
  const existing = readEnrollment(storage)
  if (existing) return existing
  const record: EnrollmentRecord = {
    enrollment_id: init.newId(),
    first_seen_at: new Date(init.nowMs).toISOString(),
    utm_campaign: init.utmCampaign || null,
    variant: init.variant || 'organic',
    experiment_version: experimentVersionFor(init.variant),
  }
  try {
    storage?.setItem(ENROLLMENT_KEY, JSON.stringify(record))
  } catch {
    // private mode / quota — keep the in-memory record for this load
  }
  return record
}

/**
 * Put a page-held record back when storage has lost it. iOS WebKit (mostly
 * the FB/IG in-app browser) can drop the origin's localStorage while the page
 * is open (Oct 2026); without this a later reload starts a second enrollment
 * and forgets the ad click. A value that is present is never overwritten;
 * unusable storage is ignored (the in-memory copy still holds for this page).
 */
export function restoreIfMissing(storage: KVStorage | null, key: string, record: string | null): void {
  if (!storage || record === null) return
  try {
    if (storage.getItem(key) === null) storage.setItem(key, record)
  } catch {
    // private mode / quota / SecurityError
  }
}

/** Add experiment_version + enrollment_id to any analytics params object. */
export function withEnrollmentParams<T extends Record<string, unknown>>(
  params: T | undefined,
  enrollment: EnrollmentRecord | null,
): T & { experiment_version: string; enrollment_id: string } {
  return {
    ...(params || ({} as T)),
    experiment_version: enrollment?.experiment_version || DEFAULT_EXPERIMENT_VERSION,
    enrollment_id: enrollment?.enrollment_id || 'unknown',
  }
}

// ───────────────────────── A4: onboarding step list version ─────────────────────────

/**
 * Identity of the active step list. The persisted step INDEX is only
 * meaningful for the list it was saved against; when this changes (a deploy
 * reorders steps, or the browser switches variant) the saved index resets.
 */
export function stepsVersionFor(variant: string, stepKeys: readonly string[]): string {
  let h = 0
  const s = stepKeys.join(',')
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0
  return `${variant}:${stepKeys.length}:${(h >>> 0).toString(36)}`
}
